import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5006/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Rate limiting: Track request timestamps
const requestTracker = new Map();
const RATE_LIMIT_WINDOW = 1000; // 1 second
const MAX_REQUESTS_PER_WINDOW = 30; // 30 requests per second (increased from 10)

// Add token to requests and rate limiting
api.interceptors.request.use((config) => {
  // Rate limiting check
  const now = Date.now();
  const endpoint = `${config.method?.toUpperCase()} ${config.url}`;
  
  // Clean old entries
  for (const [key, timestamps] of requestTracker.entries()) {
    const filtered = timestamps.filter(time => now - time < RATE_LIMIT_WINDOW);
    if (filtered.length === 0) {
      requestTracker.delete(key);
    } else {
      requestTracker.set(key, filtered);
    }
  }
  
  // Check current endpoint rate
  const currentRequests = requestTracker.get(endpoint) || [];
  if (currentRequests.length >= MAX_REQUESTS_PER_WINDOW) {
    console.warn(`🚫 Rate limit exceeded for ${endpoint}. Blocking request.`);
    return Promise.reject(new Error(`Rate limit exceeded for ${endpoint}`));
  }
  
  // Track this request
  currentRequests.push(now);
  requestTracker.set(endpoint, currentRequests);
  
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration and rate limiting
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    } else if (error.response?.status === 429) {
      console.warn('🚫 Rate limit hit:', error.config?.url);
      // Don't redirect or throw for rate limits, just log and reject
      const retryAfter = error.response.headers['retry-after'] || 5;
      console.log(`⏱️ Rate limited. Retry after ${retryAfter} seconds.`);
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password) => api.post('/auth/register', { name, email, password }),
  getCurrentUser: () => api.get('/auth/me'),
};

// Site services
export const siteService = {
  getSites: () => api.get('/sites'),
  getSite: (id) => api.get(`/sites/${id}`),
  createSite: (data) => api.post('/sites', data),
  updateSite: (id, data) => api.put(`/sites/${id}`, data),
  deleteSite: (id) => api.delete(`/sites/${id}`),
  publishSite: (id) => api.post(`/sites/${id}/publish`, {}, { 
    timeout: 400000 // 4 minutes timeout for publish operations (includes multilingual translation)
  }),
};

// AI services
export const aiService = {
  generateContent: (businessInfo, sections) => 
    api.post('/ai/generate-content', { businessInfo, sections }),
  generateSEO: (businessInfo, sections) => 
    api.post('/ai/generate-seo', { businessInfo, sections }),
  improveContent: (content, instruction) => 
    api.post('/ai/improve-content', { content, instruction }),
  healthCheck: () => api.get('/ai/health'),
};

export default api;